import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { t } from '../../../../common/i18n';
import { isEppSolutionActive } from '../../../../common/utils/stateUtils';
import { OnlineModelCategory } from '../../../../generated/api/onlineModelCategory';
import { isActiveEppPrice, isSelfServiceModel } from '../../../ShoppingBasket/shoppingBasketUtils';
import { isYttProduct } from '../../utils/placeOrderUtils';
import { needsSubscriptionConfiguration } from '../../utils/subscriptionConfigurationUtils';
import { AddOnSelection } from './AddOnSelection';
import { ContactOrPurposeWrapper } from './ContactOrPurposeWrapper';
import { KeyUserDeviceChangeHandler } from './KeyUserDeviceChangeHandler';
import { SubscriptionConfigurationHandler } from './SubscriptionConfigurationHandler';
import { YttAdditionalFields } from './YttAdditionalFields';
import type { YttAdditionalFieldsHandle } from './YttAdditionalFields';
import type { PlaceOrderProps } from '../../PlaceOrder';
import type { ContactsUpdateMethods, PlaceOrderFormData, PlaceOrderItemUnit } from '../../placeOrderTypes';
import type { ContactOrPurposeHandle } from './PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose';
import type { SubscriptionConfigurationHandle } from './SubscriptionConfigurationHandler';
import type { BasketItem } from '../../../../common/types/shoppingBasket';
import type { Offer } from '../../../../generated/api/offer';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type { Subscription } from '../../../../generated/api/subscription';
import type { RefObject } from 'react';

export interface OrderItemUnitsProps extends PlaceOrderProps {
  orderItemUnits: PlaceOrderItemUnit[];
  basketItem: BasketItem;
  onlineModel: OnlineModel;
  mdmId: string;
  offer: Offer;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  expiringEppSubscriptions: Subscription[];
  subscriptionConfigRefs: RefObject<Record<string, SubscriptionConfigurationHandle | null>>;
  yttFieldRefs: RefObject<Record<string, YttAdditionalFieldsHandle | null>>;
  isFirstBasketItem: boolean;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  contactsUpdateMethods: ContactsUpdateMethods;
}

export const OrderItemUnits = ({
  orderItemUnits,
  basketItem,
  onlineModel,
  offer,
  user,
  companyInfo,
  contactFormRefs,
  mdmId,
  expiringEppSubscriptions,
  subscriptionConfigRefs,
  yttFieldRefs,
  phoneNumbers,
  phoneNumbersLoading,
  onLoadMoreNumbers,
  shoppingBasketUpdateMethods: { updateAddons },
  contactsUpdateMethods,
  isFirstBasketItem,
}: OrderItemUnitsProps) => {
  const methods = useFormContext<PlaceOrderFormData>();

  // Collect all used SIM card numbers for duplicate validation
  const allOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];
  const usedSimCardNumbers = allOrderItemUnits.map(unit => unit.simCardNumber).filter((num): num is string => !!num);

  // Collect phone numbers already selected by other items (for disabling in list)
  const getDisabledPhoneNumbers = (globalIndex: number): string[] => {
    return allOrderItemUnits
      .filter((_, idx) => idx !== globalIndex)
      .map(unit => unit.mobileNumber)
      .filter((num): num is string => !!num);
  };

  // Get initial phone number for subscription at given global index
  // This ensures each subscription gets a unique phone number when multiple subscriptions are added from basket
  const getInitialPhoneNumber = (globalIndex: number): string | undefined => {
    return needsSubscriptionConfiguration(onlineModel) && phoneNumbers.length > globalIndex
      ? phoneNumbers[globalIndex]
      : undefined;
  };

  // Find global index in flattened placeOrderItemUnits array for a given orderItem
  const getGlobalIndex = (orderItem: PlaceOrderItemUnit): number => {
    return allOrderItemUnits.findIndex(
      unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
    );
  };

  const isDevice = ![OnlineModelCategory.SALES_PRODUCT, OnlineModelCategory.SELF_SERVICE].includes(
    onlineModel.category as OnlineModelCategory
  );

  const isSubscription = isSelfServiceModel(onlineModel);
  const isEppDevice =
    isEppSolutionActive(companyInfo) && isActiveEppPrice(basketItem.offer.commercialProductGuid, onlineModel);
  const isInternalCustomer = companyInfo?.customerType === 'INTERNAL_CUSTOMERS';
  const isSalesProductWithPurposeOfUse =
    onlineModel.category === OnlineModelCategory.SALES_PRODUCT &&
    (onlineModel.parameters as { purposeOfUse?: boolean })?.purposeOfUse === true;

  const isYtt = isYttProduct(onlineModel);

  // Subscriptions: no accordion, always mandatory
  // Devices (EPP/internal): no accordion, mandatory
  // Devices (regular): accordion, optional
  const showAccordion = !isSubscription && !isEppDevice && !isInternalCustomer;
  const isMandatory = isSubscription || isEppDevice || isInternalCustomer || isSalesProductWithPurposeOfUse;

  const mappedOrderItems = orderItemUnits.map((orderItem: PlaceOrderItemUnit) => {
    // Calculate global index for this orderItem across all basket items
    const globalIndex = getGlobalIndex(orderItem);
    const selectedContactId = methods.watch(`products.placeOrderItemUnits.${globalIndex}.userContact`);

    return (
      <li key={`${basketItem.id}-${orderItem.basketItemIndex}`} className="of-order-item-unit">
        <div className={dsClass.PADDING_VERTICAL_4}>
          <h3>
            {isDevice
              ? t.CL0O('Device {} details', String(orderItem.basketItemIndex + 1))
              : t.KTY1('Subscription {} details', String(orderItem.basketItemIndex + 1))}
          </h3>
          <ContactOrPurposeWrapper
            showAccordion={showAccordion}
            isMandatory={isMandatory}
            isCostCenterRequired={isInternalCustomer}
            contactFormRefs={contactFormRefs}
            orderItem={orderItem}
            refId={`${basketItem.id}-${orderItem.basketItemIndex}`}
            contactsUpdateMethods={contactsUpdateMethods}
          />
          {isEppDevice && selectedContactId && (
            <KeyUserDeviceChangeHandler
              orderItem={orderItem}
              refId={`${basketItem.id}-${orderItem.basketItemIndex}`}
              companyInfo={companyInfo}
              expiringEppSubscriptions={expiringEppSubscriptions}
              isFirstOrderItem={isFirstBasketItem && orderItem.basketItemIndex === 0}
              commercialProductCode={basketItem.offer.commercialProductGuid}
              selectedContactId={selectedContactId}
            />
          )}
          {needsSubscriptionConfiguration(onlineModel) && companyInfo.companyName && (
            <SubscriptionConfigurationHandler
              ref={el => {
                subscriptionConfigRefs.current[`${basketItem.id}-${orderItem.basketItemIndex}`] = el;
              }}
              cartItemInstanceId={`${basketItem.id}-${orderItem.basketItemIndex}`}
              onlineModel={onlineModel}
              companyName={companyInfo.companyName}
              basketItem={basketItem}
              mdmId={mdmId}
              phoneNumbers={phoneNumbers}
              phoneNumbersLoading={phoneNumbersLoading}
              onLoadMoreNumbers={onLoadMoreNumbers}
              usedSimCardNumbers={usedSimCardNumbers}
              disabledPhoneNumbers={getDisabledPhoneNumbers(globalIndex)}
              initialPhoneNumber={getInitialPhoneNumber(globalIndex)}
            />
          )}
          {isYtt && (
            <YttAdditionalFields
              ref={el => {
                yttFieldRefs.current[`${basketItem.id}-${orderItem.basketItemIndex}`] = el;
              }}
              globalIndex={globalIndex}
            />
          )}
        </div>

        <AddOnSelection
          itemIndex={orderItem.basketItemIndex}
          orderItem={orderItem}
          onlineModel={onlineModel}
          basketItem={basketItem}
          mdmId={mdmId}
          offer={offer}
          user={user}
          companyInfo={companyInfo}
          updateAddons={updateAddons}
        />
      </li>
    );
  });

  return <ul className={dsClass.PADDING_LEFT_0}>{mappedOrderItems}</ul>;
};
